import Axios from 'axios';
import * as Routes from '@/modules/routes';
import serializeParams from '@/utils/serializeParams';
import type { RansackSearch } from '@/components/admin/SysadminSPA/shared/filterTypes';
import useProgressiveLoad from '@/components/admin/SysadminSPA/shared/useProgressiveLoad';
import QueryKeys from '@/utils/QueryKeys';

export type AlertMessageKind = 'lesson' | 'program';
export type AlertMessageType = 'HeaderNotification' | 'InfoBar';

export interface AlertMessage {
  appliesTo: string;
  category: string;
  content: string;
  endDate: string | null;
  forReviewOnly: boolean;
  id: number;
  kind: AlertMessageKind;
  location: string;
  onPageLoad: boolean;
  published: boolean;
  purpose: string;
  statesSelected: string;
  title: string;
  type: AlertMessageType;
  typeLabel: string;
}

export interface RawAlertMessage {
  association_summary: string;
  category: string | null;
  content: string | null;
  end_date: string | null;
  for_review_only: boolean;
  id: number;
  kind: AlertMessageKind;
  location: string | null;
  on_page_load: boolean;
  publish: boolean | null;
  purpose: string | null;
  states_selected: string;
  title: string;
  type: AlertMessageType;
  type_label: string;
}

export interface AlertMessagesPage {
  data: RawAlertMessage[];
  meta: { count: number };
}

const FETCH_PAGE_SIZE = 100;

const ONLY = [
  'id', 'title', 'type', 'type_label', 'category', 'kind', 'location', 'on_page_load', 'for_review_only',
  'publish', 'purpose', 'end_date', 'content', 'states_selected', 'association_summary',
];

const fetchPage = async (page: number, search: RansackSearch): Promise<AlertMessagesPage> => {
  const params: Record<string, unknown> = {
    includes: ['programs', 'lessons', 'states'],
    only: ONLY,
    page,
    per_page: FETCH_PAGE_SIZE,
  };

  if (Object.keys(search).length > 0) {
    params.search = search;
  }

  const response = await Axios.get(`${Routes.plato_api_alert_messages_path()}?${serializeParams(params)}`);

  return response.data;
};

const transform = (raw: RawAlertMessage): AlertMessage => ({
  appliesTo: raw.association_summary,
  category: raw.category ?? '',
  content: raw.content ?? '',
  endDate: raw.end_date,
  forReviewOnly: raw.for_review_only,
  id: raw.id,
  kind: raw.kind,
  location: raw.location ?? '',
  onPageLoad: raw.on_page_load,
  published: Boolean(raw.publish),
  purpose: raw.purpose ?? '',
  statesSelected: raw.states_selected,
  title: raw.title,
  type: raw.type,
  typeLabel: raw.type_label,
});

const useContentAlertMessages = (search: RansackSearch = {}) => (
  useProgressiveLoad<RawAlertMessage, AlertMessage>({
    fetchPage: page => fetchPage(page, search),
    queryKey: [QueryKeys.ContentAlertMessages, search],
    transform,
  })
);

export default useContentAlertMessages;
